import React from 'react';
import { CalendarDays, Factory, ArrowRight, LogOut, Shield, Eye, Package } from 'lucide-react';

export default function ModuleSelector({ onSelectModule, user, onLogout }) {
  return (
    <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center p-8" data-testid="module-selector">
      {/* User bar */}
      <div className="fixed top-0 right-0 p-4 flex items-center gap-3">
        <div className="flex items-center gap-2 bg-slate-800/80 backdrop-blur border border-slate-700/50 rounded-lg px-3 py-2">
          <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${user?.role === 'admin' ? 'bg-amber-600 text-white' : 'bg-slate-600 text-slate-200'}`}>
            {(user?.name || 'U')[0].toUpperCase()}
          </div>
          <span className="text-xs text-slate-300 font-medium">{user?.name || 'User'}</span>
          <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[9px] font-bold rounded-full ${user?.role === 'admin' ? 'bg-amber-500/20 text-amber-400' : 'bg-slate-700 text-slate-400'}`}>
            {user?.role === 'admin' ? <Shield className="w-2.5 h-2.5" /> : <Eye className="w-2.5 h-2.5" />}
            {user?.role === 'admin' ? 'Admin' : 'Viewer'}
          </span>
          <button onClick={onLogout} className="p-1 hover:bg-slate-700 rounded text-slate-500 hover:text-red-400 ml-1" title="Logout" data-testid="module-logout-btn">
            <LogOut className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
      {/* Logo */}
      <div className="mb-12 text-center">
        <img src="/images/kiel-logo.png" alt="Kiel Americas" className="h-16 w-auto mx-auto mb-4" />
        <h1 className="text-2xl font-bold text-white tracking-tight">Kiel Americas Management</h1>
        <p className="text-sm text-slate-500 mt-1">Production & Operations Management System</p>
      </div>

      {/* Module Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl w-full">
        {/* Planning Module */}
        <button onClick={() => onSelectModule('planning')}
          className="group relative bg-slate-900 border border-slate-800 rounded-2xl p-8 text-left transition-all duration-300 hover:border-amber-500/50 hover:bg-slate-900/80 hover:shadow-[0_0_40px_rgba(245,158,11,0.08)]"
          data-testid="module-planning">
          <div className="w-14 h-14 rounded-xl bg-amber-500/10 flex items-center justify-center mb-5">
            <CalendarDays className="w-7 h-7 text-amber-500" />
          </div>
          <h2 className="text-xl font-bold text-white mb-2">Production Planning</h2>
          <p className="text-sm text-slate-400 leading-relaxed mb-6">
            Plan production schedules, track orders, manage shipments and generate daily reports for team leaders.
          </p>
          <div className="flex items-center gap-2 text-amber-500 text-sm font-semibold">
            <span>Open Planning</span>
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
          </div>
          <div className="absolute top-4 right-4">
            <span className="px-2 py-1 text-[10px] font-bold bg-amber-500/10 text-amber-500 rounded-full uppercase tracking-wider">Active</span>
          </div>
        </button>

        {/* Production Simulation Module */}
        <button onClick={() => onSelectModule('production')}
          className="group relative bg-slate-900 border border-slate-800 rounded-2xl p-8 text-left transition-all duration-300 hover:border-blue-500/50 hover:bg-slate-900/80 hover:shadow-[0_0_40px_rgba(59,130,246,0.08)]"
          data-testid="module-production">
          <div className="w-14 h-14 rounded-xl bg-blue-500/10 flex items-center justify-center mb-5">
            <Factory className="w-7 h-7 text-blue-500" />
          </div>
          <h2 className="text-xl font-bold text-white mb-2">Production Simulation</h2>
          <p className="text-sm text-slate-400 leading-relaxed mb-6">
            Line balancing, operations management, personnel capacity and shift planning.
          </p>
          <div className="flex items-center gap-2 text-blue-500 text-sm font-semibold">
            <span>Open Simulation</span>
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
          </div>
          <div className="absolute top-4 right-4">
            <span className="px-2 py-1 text-[10px] font-bold bg-blue-500/10 text-blue-500 rounded-full uppercase tracking-wider">Stable</span>
          </div>
        </button>

        {/* Warehouse Module */}
        <button onClick={() => onSelectModule('warehouse')}
          className="group relative bg-slate-900 border border-slate-800 rounded-2xl p-8 text-left transition-all duration-300 hover:border-emerald-500/50 hover:bg-slate-900/80 hover:shadow-[0_0_40px_rgba(16,185,129,0.08)]"
          data-testid="module-warehouse">
          <div className="w-14 h-14 rounded-xl bg-emerald-500/10 flex items-center justify-center mb-5">
            <Package className="w-7 h-7 text-emerald-500" />
          </div>
          <h2 className="text-xl font-bold text-white mb-2">Sage Data Tools</h2>
          <p className="text-sm text-slate-400 leading-relaxed mb-6">
            Pick Ticket summaries, Component Requirement Report converter, and component database management.
          </p>
          <div className="flex items-center gap-2 text-emerald-500 text-sm font-semibold">
            <span>Open Sage Tools</span>
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
          </div>
          <div className="absolute top-4 right-4">
            <span className="px-2 py-1 text-[10px] font-bold bg-emerald-500/10 text-emerald-500 rounded-full uppercase tracking-wider">New</span>
          </div>
        </button>
      </div>

      {/* Footer */}
      <p className="mt-12 text-xs text-slate-600">Kiel Americas Management v2.0</p>
    </div>
  );
}
